import { reverse } from 'named-urls';

import React from 'react';

import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

import { observer } from 'mobx-react';

import { useParams } from 'react-router-dom';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Box, Tooltip } from '@mui/material';

import MainHeader from 'components/MainHeader';
import routes from 'core/routes';

import Record from 'ui/Record/Record';
import { useNavBarHeight } from 'utils/hooks/navbar';
import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, EntityModules } from 'core/constants';
import { Button } from 'components/Button';
import { LastPublished } from 'containers/LayoutEditor/styles';
import { formatMoment } from 'utils/formatters/timezone';

import { useStore } from 'utils/hooks/store';
import { CenteredCircularProgress } from 'components/Progress';

import DataError from 'containers/Error/DataError';
import DetailsLoader from 'ui/DetailsLoader';

import { isNotFound } from 'utils/api/errors';

import NotFound from 'containers/Error/Pages/NotFound/NotFound';

import NavigationBlocker from 'components/NavigationBlocker';

import { colors } from 'theme/colors';

import StageDetails from './components/StageDetails';
import RelevantFields from './components/RelevantFields';
import StagesDragAndDrop from './components/StagesDragAndDrop/StagesDragAndDrop';
import AddModalForCreatingStage from './components/AddModalForCreatingStage';
import EditableHeader from './components/EditableHeader';

const PipelinesDetails = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const {
    pipelineDetails,
    pipelineDetails: { isModalOpenForCreatingStage, data, onDeleteStage },
    routing,
    session: { user },
  } = useStore();
  const params = useParams<{ pipelineId: string }>();
  const pipelineId = params.pipelineId as string;

  const redirectToHelpCenter = (): void => {
    window.open(user?.helpCenterUrls.pipelineUrl, '_blank', 'noopener,noreferrer');
  };

  const onCancel = (): void => {
    routing.push(
      reverse(routes.dashboard.settings.objects.details.pipelines.toString(), {
        moduleName: EntityModules.Events,
      })
    );
  };

  const onSave = (): void => {
    pipelineDetails.publishPipeline(pipelineId);
  };

  const isClosedStage = React.useMemo(
    () => pipelineDetails.data?.stages.find((stage) => stage.key === pipelineDetails.selectedStage)?.closed ?? false,
    [pipelineDetails.data, pipelineDetails.selectedStage]
  );

  const isStageUsedInEntities = React.useMemo(
    () =>
      pipelineDetails.data?.stages.find((stage) => stage.key === pipelineDetails.selectedStage)
        ?.stageIsUsedInEntities ?? false,
    [pipelineDetails.data, pipelineDetails.selectedStage]
  );

  return (
    <>
      <NavigationBlocker
        when={pipelineDetails.contentWasModified}
        shouldBlockNavigation={() => pipelineDetails.contentWasModified}
        navigateMessage="You have unsaved changes. Do you want to discard them or stay on this page?"
      />
      <Button
        size="large"
        variant="text"
        onClick={redirectToHelpCenter}
        endIcon={<HelpOutlineOutlinedIcon />}
        style={{
          top: '50%',
          right: '0px',
          position: 'fixed',
          marginRight: '4px',
          background: colors.tint.primary.light,
          transform: 'translateY(-50%)  rotate(-90deg)',
        }}
      >
        Help Centre
      </Button>

      <DetailsLoader params={params} routeParam="pipelineId" store={pipelineDetails}>
        {({ isLoaded, error, reload }): React.ReactElement => {
          if (!isLoaded) {
            return <CenteredCircularProgress />;
          }
          if (pipelineDetails.data === undefined) {
            return <NotFound />;
          }
          if (error) {
            if (isNotFound(error)) {
              return <NotFound />;
            }
            return <DataError onReload={reload} />;
          }

          const name = data?.name ?? 'N/A';
          const lastPublished = data?.updatedAt ?? new Date();

          return (
            <Record
              disableMaxContentHeight
              navBarHeight={navBarHeight}
              Header={
                <MainHeader
                  customHeaderTitle={<EditableHeader pipeline={data} />}
                  title={name}
                  breadcrumbs={[
                    {
                      label: 'Objects',
                      link: '',
                    },
                    {
                      label: 'Events',
                      link: reverse(routes.dashboard.settings.objects.details.formBuilder.toString(), {
                        moduleName: EntityModules.Events,
                      }),
                    },
                    {
                      label: 'Pipelines',
                      link: reverse(routes.dashboard.settings.objects.details.pipelines.toString(), {
                        moduleName: EntityModules.Events,
                      }),
                    },
                    {
                      label: name,
                      link: reverse(routes.dashboard.settings.objects.details.pipelines.details.toString(), {
                        moduleName: EntityModules.Events,
                        pipelineId: '1',
                      }),
                    },
                  ]}
                  action={
                    <>
                      <LastPublished data-testid="last-published">
                        {`Last published: ${formatMoment(lastPublished, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}`}
                      </LastPublished>
                      <Button size="large" variant="outlined" onClick={onCancel} sx={{ margin: '4px 0' }}>
                        Cancel
                      </Button>
                      <Button
                        size="large"
                        variant="contained"
                        onClick={onSave}
                        sx={{ marginLeft: '16px' }}
                        disabled={!pipelineDetails.contentWasModified}
                      >
                        Publish
                      </Button>
                    </>
                  }
                />
              }
              Content={() => (
                <>
                  <StagesDragAndDrop />
                  <Box sx={{ width: '70%', margin: '0 auto', marginTop: '12px' }}>
                    <StageDetails />
                    <RelevantFields />
                    {/* don't display the button when it's closed stage */}
                    {!isClosedStage && (
                      <Tooltip
                        title={
                          isStageUsedInEntities
                            ? 'Stage cannot be deactivated as it is currently assigned to one or more entities. Please reassign the entities first, then try again.'
                            : undefined
                        }
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Button
                            size="large"
                            startIcon={<DeleteOutlineIcon />}
                            variant="outlined"
                            onClick={onDeleteStage}
                            disabled={isStageUsedInEntities || pipelineDetails.data?.order.length === 2}
                            color={pipelineDetails.data?.order.length === 2 ? 'secondary' : 'error'}
                          >
                            Delete stage
                          </Button>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  <AddModalForCreatingStage open={isModalOpenForCreatingStage} />
                </>
              )}
            />
          );
        }}
      </DetailsLoader>
    </>
  );
};

export default observer(PipelinesDetails);
